import { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import StatCard from './components/StatCard';
import RecentActivity from './components/RecentActivity';
import RecentUsers from './components/RecentUsers';
import RegistrationsChart from './components/RegistrationsChart';
import { dashboardService, type DashboardStats } from '../../../services/dashboard.service';
import { registrationService } from '../../../services/registration.service';
import { useAuth } from '../../../auth/AuthContext';

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [totalRegistered, setTotalRegistered] = useState(0);
  const [unemployedCount, setUnemployedCount] = useState(0);

  useEffect(() => {
    dashboardService.stats().then(setStats);
    registrationService.list().then((regs) => {
      setTotalRegistered(regs.length);
      setUnemployedCount(regs.filter((r) => r.unemployed_over_year).length);
    });
  }, []);

  const thisMonth = stats?.totalUsers ?? 0;
  const activeActivities = stats?.activeActivities ?? 0;
  const openJobs = stats?.openJobs ?? 0;
  const registeredCount = totalRegistered;
  const unemployed = unemployedCount;

  return (
    <AdminLayout title="דשבורד ראשי" subtitle="ברוכים הבאים למערכת הניהול">
      <div className="bg-white rounded-2xl border-2 border-rose-200 p-5 mb-6 flex items-center justify-between overflow-hidden relative">
        <div className="relative z-10">
          <p className="text-xs text-rose-400 font-semibold mb-1">
            {new Date().toLocaleDateString('he-IL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
          <h2 className="text-lg font-bold text-gray-800">שלום, {user?.name}! 👋</h2>
          <p className="text-sm text-gray-500 mt-1">{thisMonth} נרשמים חדשים החודש ו-{activeActivities} פעילויות פעילות מחכות.</p>
        </div>
        <div className="hidden md:flex items-center gap-2">
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-rose-50 border-2 border-rose-100">
            <i className="ri-bar-chart-grouped-line text-3xl text-rose-400"></i>
          </div>
        </div>
        <div className="absolute -left-6 -bottom-6 w-32 h-32 rounded-full bg-rose-50 opacity-60"></div>
        <div className="absolute -left-2 -top-4 w-20 h-20 rounded-full bg-amber-50 opacity-40"></div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="נרשמים החודש" value={thisMonth} icon="ri-user-add-line" color="text-rose-500" bgColor="bg-rose-50" borderColor="border-rose-100" trend="+18%" trendUp />
        <StatCard label="סה״כ משתתפים בפעילויות" value={registeredCount} icon="ri-group-line" color="text-violet-500" bgColor="bg-violet-50" borderColor="border-violet-100" trend={`${activeActivities} פעילויות פעילות`} trendUp />
        <StatCard label="אחוז מילוי ממוצע" value={`${activeActivities ? Math.round((registeredCount / (activeActivities * 30)) * 100) : 0}%`} icon="ri-pie-chart-2-line" color="text-amber-500" bgColor="bg-amber-50" borderColor="border-amber-100" trend={`${openJobs} משרות פתוחות`} trendUp />
        <StatCard label="מובטלים מעל שנה" value={unemployed} icon="ri-time-line" color="text-emerald-500" bgColor="bg-emerald-50" borderColor="border-emerald-100" trend={`${registeredCount ? Math.round((unemployed / registeredCount) * 100) : 0}% מהנרשמים`} trendUp={false} />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'הוסף פעילות', icon: 'ri-calendar-2-line', color: 'text-violet-500 bg-violet-50 border-violet-200', link: '/admin/activities' },
          { label: 'הוסף משרה', icon: 'ri-briefcase-4-line', color: 'text-amber-500 bg-amber-50 border-amber-200', link: '/admin/jobs' },
          { label: 'צפה בנרשמים', icon: 'ri-group-2-line', color: 'text-emerald-500 bg-emerald-50 border-emerald-200', link: '/admin/users' },
        ].map((action) => (
          <a key={action.label} href={action.link}
            className={`flex items-center gap-3 p-4 rounded-2xl border-2 ${action.color} hover:scale-[1.02] transition-all cursor-pointer`}>
            <div className="w-8 h-8 flex items-center justify-center"><i className={`${action.icon} text-xl`}></i></div>
            <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">{action.label}</span>
          </a>
        ))}
      </div>

      <div className="mb-5"><RegistrationsChart /></div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <RecentActivity />
        <RecentUsers />
      </div>
    </AdminLayout>
  );
}
